import { forwardRef, useId, useState, useCallback, useEffect, useRef } from 'react';
import type {
    ChangeEventHandler,
    ComponentPropsWithoutRef,
    FocusEventHandler,
    MutableRefObject,
    ReactNode,
} from 'react';
import { twMerge } from 'tailwind-merge';
import RequiredBadge from '../../visual-helper/RequiredBadge';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { REQUIRED_FIELD_MESSAGE, REQUIRED_VALIDATION_EVENT, type RequiredValidationEventDetail } from '@/chassis/data/services/form-required-validation';

type I18nLabel = Record<Language, string>;
type I18nLike = string | I18nLabel;

const toI18nLabel = (value?: I18nLike, lang?: Language): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return lang ? value[lang] : undefined;
};

type CheckboxInputProps = ComponentPropsWithoutRef<'input'>;

const baseInputClasses = [
    'peer',
    'transition-all duration-100 ease-in-out',
    'shadow-sm border-slate-300/80 cursor-pointer rounded focus:ring-4 focus:ring-offset-0 focus:ring-primary focus:ring-opacity-20 dark:bg-darkmode-700 dark:focus:ring-slate-700 dark:focus:ring-opacity-50',
    "[&[type='checkbox']]:checked:bg-primary/60 [&[type='checkbox']]:checked:border-primary/50 [&[type='checkbox']]:checked:border-opacity-10",
    '[&:disabled:not(:checked)]:bg-slate-100 [&:disabled:not(:checked)]:cursor-not-allowed [&:disabled:not(:checked)]:dark:bg-darkmode-700/50',
    '[&:disabled:checked]:opacity-70 [&:disabled:checked]:cursor-not-allowed [&:disabled:checked]:dark:bg-darkmode-700/50',
];

const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>(function CheckboxInput(props, ref) {
    return <input type="checkbox" ref={ref} {...props} className={twMerge([...baseInputClasses, props.className])} />;
});

type CheckboxLabelProps = ComponentPropsWithoutRef<'label'> & {
    i18nLabel?: I18nLike;
};

function CheckboxLabel({ i18nLabel, children, ...rest }: CheckboxLabelProps) {
    const lang = useLanguage();
    const displayContent = toI18nLabel(i18nLabel, lang) ?? children;
    return (
        <label {...rest} className={twMerge(['cursor-pointer ml-2 inline-flex items-center', rest.className])}>
            {displayContent}
        </label>
    );
}

export interface FormCheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    label?: ReactNode;
    i18nLabel?: I18nLike;
    wrapperClassName?: string;
    requiredMessage?: string;
}

export const FormCheckbox = forwardRef<HTMLInputElement, FormCheckboxProps>(function FormCheckbox(
    { label, i18nLabel, wrapperClassName, id, name, className, onBlur, onChange, requiredMessage = REQUIRED_FIELD_MESSAGE, ...rest },
    ref
) {
    const lang = useLanguage();
    const displayLabel = toI18nLabel(i18nLabel, lang) ?? label;
    const { ['aria-describedby']: ariaDescribedByProp, ...restProps } = rest as typeof rest & {
        'aria-describedby'?: string;
    };
    const isRequired = Boolean((restProps as { required?: boolean }).required);
    const [showRequiredError, setShowRequiredError] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const generatedId = useId();
    const controlId = id || (name as string | undefined) || generatedId;
    const errorId = `${controlId}-required`;
    const ariaDescribedBy = [ariaDescribedByProp, showRequiredError ? errorId : null]
        .filter(Boolean)
        .join(' ') || undefined;
    const badge = isRequired ? <RequiredBadge className="ml-2" /> : null;
    const inputClassName = twMerge([className, showRequiredError && 'border-danger focus:border-danger focus:ring-danger/30']);

    const mergeRefs = useCallback(
        (node: HTMLInputElement | null) => {
            inputRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                (ref as MutableRefObject<HTMLInputElement | null>).current = node;
            }
        },
        [ref],
    );

    const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
        if (isRequired) {
            setShowRequiredError(!event.target.checked);
        }
        onBlur?.(event);
    };

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (isRequired && showRequiredError && event.target.checked) {
            setShowRequiredError(false);
        }
        onChange?.(event);
    };

    useEffect(() => {
        if (!isRequired) return undefined;

        const listener = (event: Event) => {
            const detail = (event as CustomEvent<RequiredValidationEventDetail>).detail;
            const targetForm = detail?.form;
            const element = inputRef.current;

            if (!element) return;
            if (targetForm && element.form && targetForm !== element.form) return;

            if (!element.checked) {
                setShowRequiredError(true);
            }
        };

        window.addEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
        return () => window.removeEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
    }, [isRequired]);

    const helpText = showRequiredError ? (
        <p id={errorId} className="mt-1 basis-full pl-7 text-xs text-danger">
            {requiredMessage}
        </p>
    ) : null;

    return (
        <div className={twMerge(['flex flex-wrap items-center', wrapperClassName])}>
            <CheckboxInput
                ref={mergeRefs}
                id={controlId}
                name={name}
                className={inputClassName}
                onBlur={handleBlur}
                onChange={handleChange}
                aria-invalid={showRequiredError ? 'true' : undefined}
                aria-describedby={ariaDescribedBy}
                {...restProps}
            />
            {displayLabel ? (
                <CheckboxLabel htmlFor={controlId} i18nLabel={i18nLabel}>
                    <span>{displayLabel}</span>
                    {badge}
                </CheckboxLabel>
            ) : null}
            {helpText}
        </div>
    );
});

export default FormCheckbox;
